import React, { useEffect } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { useLogin } from '@pkg/hooks';

import { type Token } from '@app/client/common/structs';
import { AuthProviders } from '@app/client/resources/w3auth/types';
import { callGenericProviderSignIn, callTwitterProviderSignIn } from '@app/client/resources/w3auth/w3auth';
import { AUTH_SIGN_IN } from '@app/routes/paths';

import { AuthNewLocalStorage } from './Web3Auth.types';

enum AuthSearchParams {
    OAuthToken = 'oauth_token',
    OAuthVerifier = 'oauth_verifier',
    Code = 'code',
    State = 'state',
    Scope = 'scope',
    AuthUser = 'authuser',
    Hd = 'hd',
    Prompt = 'prompt',
}

export interface Web3AuthCallbackProps {
    provider: AuthProviders;
}

export function Web3AuthCallback({ provider }: Web3AuthCallbackProps) {
    const [searchParams] = useSearchParams();

    const navigate = useNavigate();
    const { loginWithToken } = useLogin();

    const oAuthToken = searchParams.get(AuthSearchParams.OAuthToken);
    const oAuthVerifier = searchParams.get(AuthSearchParams.OAuthVerifier);
    const code = searchParams.get(AuthSearchParams.Code);
    const state = searchParams.get(AuthSearchParams.State);

    useEffect(() => {
        (async () => {
            try {
                let token: Token | undefined;

                // For Twitter SSO authentication
                if (provider === AuthProviders.Twitter && oAuthToken && oAuthVerifier) {
                    token = await callTwitterProviderSignIn({ oAuthToken, oAuthVerifier });
                } else if (code && state) {
                    token = await callGenericProviderSignIn(provider, { code, state });
                }

                if (token) {
                    localStorage.setItem(AuthNewLocalStorage.OAuthLoggedIn, '1');
                    await loginWithToken({ token });
                } else {
                    throw new Error('No valid token available');
                }
            } catch (error) {
                /* No valid oAuth token or not successful trying to retrieve
                 * a valid access token so redirect user to authentication page
                 */
                navigate(AUTH_SIGN_IN);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [oAuthToken, oAuthVerifier, code, state]);

    return <div />;
}
